import { useCallback, useState } from 'react';

import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useRecommendedSiteName } from '@/components/signup/site-activation/hooks/recommended-site-name';
import { useSiteActivationContext } from '@/components/signup/site-activation/hooks/site-activation-context';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';

import { LoggedInJiraNewUser } from '../components/logged-in-jira-new-user';

/**
 * Treatment view for the `spark-defer-site-name-v2` experiment (TWCG-2407).
 *
 * For new Jira free users we skip the create-site step. SiteActivation is
 * mounted with `autoActivateWithRecommendedName`, so once a recommended site
 * name is populated and validated the site is activated automatically while we
 * show a white loading screen. The confirmation redirect is handled inside the
 * activation flow.
 *
 * If fetching the recommended name fails, or the name fails its last-moment
 * availability re-check at activation time, we fall back to the existing manual
 * naming screen so the user is never left at a dead end.
 */
export const DeferSiteNameVariant = () => {
	// useContactExpandCohortLogic must match the value used by LoggedInJiraFree
	// (useCohort(true)) so the inner SiteActivationSwitcher resolves the same
	// cohort and renders our NewUser slot rather than falling through to the
	// default create-site screen.
	return (
		<SiteActivation
			useContactExpandCohortLogic
			autoActivateWithRecommendedName
			NewUser={<DeferSiteNameAutoActivate />}
		/>
	);
};

const DeferSiteNameAutoActivate = () => {
	const [hasRecommendedNameError, setHasRecommendedNameError] = useState(false);
	const { autoActivationFailed } = useSiteActivationContext();

	// Stable reference so useRecommendedSiteName's effect (which depends on
	// onError) does not re-run and re-call getRecommendedSiteName on every render.
	const onError = useCallback(() => setHasRecommendedNameError(true), []);

	// Populates the recommended site name into the SiteActivation context. The
	// provider's auto-activation effect submits once it validates.
	useRecommendedSiteName({ onError });

	// Fall back to manual naming if we could not fetch a recommended name, or if
	// the recommended name failed its last-moment availability re-check.
	if (hasRecommendedNameError || autoActivationFailed) {
		return <LoggedInJiraNewUser />;
	}

	return (
		<div css={loadingContainerCss} data-testid="defer-site-name-loading">
			<Spinner size="large" testId="defer-site-name-spinner" />
		</div>
	);
};

const loadingContainerCss = css({
	display: 'flex',
	flex: 1,
	alignItems: 'center',
	justifyContent: 'center',
	// Fill the viewport so the spinner is centered on the page while the site
	// activates, rather than sitting near the top of the content area.
	minHeight: '100vh',
	padding: token('space.400'),
});
